import { hashPath } from "../../track/utils";
import MintedAnalyticsTag from "@minted/minted-analytics-tracking-tag/src/MintedAnalyticsTag";
import { theme } from "@minted/minted-components";
import config from "config";

declare global {
  interface Window {
    mintedAnalyticsTag?: {
      emit: () => void;
      trackInteraction: (event: Record<string, unknown>) => void;
    };
  }
}

const endpoint: string = config.get("trackEndpoint");
const TRACK_READY_EVENT = "minted-analytics-track-ready";
let isTrackReady = false;

const defaultPayload = {
  feature: "wedding_site_customize",
};

const getMintedAnalyticsTag = () => {
  try {
    if (!window.mintedAnalyticsTag && endpoint) {
      const analyticsEndpointMap = {
        [window.location.host]: new URL(endpoint, window.location.href).origin,
      };

      const mintedAnalyticsTag = new MintedAnalyticsTag({
        pageViewUrlEndpointMap: analyticsEndpointMap,
        userInteractionUrlEndpointMap: analyticsEndpointMap,
      });

      window.mintedAnalyticsTag = mintedAnalyticsTag;
      isTrackReady = false;
      // Signal after a head start for /emit, not confirmed request completion.
      // Schedule before emit() so waiting events are released even if emit() throws.
      setTimeout(() => {
        isTrackReady = true;
        window.dispatchEvent(new CustomEvent(TRACK_READY_EVENT));
      }, 500);
      mintedAnalyticsTag.emit();
    }
  } catch {
    // Analytics must not block application startup.
  }

  return window.mintedAnalyticsTag;
};

if (process.env.NODE_ENV !== "test") {
  isTrackReady = Boolean(window.mintedAnalyticsTag);
  getMintedAnalyticsTag();
}

class Track {
  emit(payload: Record<string, any>) {
    const mergedPayload: Record<string, any> = {
      ...defaultPayload,
      ...payload,
    };

    if (endpoint) {
      const visitorCookie = document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith("visitorId="));

      if (visitorCookie) {
        mergedPayload.visitor_id = visitorCookie.replace("visitorId=", "");
      }

      const analytics = getMintedAnalyticsTag();
      const send = () => {
        try {
          analytics?.trackInteraction(mergedPayload);
        } catch {
          // Analytics must not block application behavior.
        }
      };

      if (!isTrackReady) {
        window.addEventListener(TRACK_READY_EVENT, send, { once: true });
      } else {
        send();
      }
    } else {
      console.info(
        "Cannot send track event, no endpoint configured - payload is: ",
        mergedPayload,
      );
    }
  }
}

const track = new Track();

export default track;

type Breakpoint = {
  breakpointName: string;
  breakpointValue: number;
};

export const trackPageLoad = (
  featureName,
  site,
  guestId = null,
  userId = "",
) => {
  const width = document.documentElement.clientWidth;
  const smallViewport: Breakpoint = theme.breakpointsList.find(
    (breakpoint: Breakpoint) => breakpoint.breakpointName === "small",
  ) as Breakpoint;
  const isMobile = width < smallViewport.breakpointValue;
  const referrer = document.referrer;

  const payload = {
    event: "page_load",
    feature: featureName,
    is_mobile: isMobile,
    referrer_domain: referrer.split("?")[0],
    referrer_hash: hashPath(referrer),
    site_id: site.id,
    site_type: site.siteType,
    url_hash: hashPath(window.location.href),
  };

  if (userId) {
    payload["minted_user_uid"] = userId;
  }

  if (guestId) {
    payload["guest_id"] = guestId;
  }

  track.emit(payload);
};
